import React from 'react';
import { useTheme } from '../context/ThemeContext';

// ── Static color constants (for backward compat) ──
export const GOLD = '#FFD700';
export const GOLD_DARK = '#B8860B';
export const SILVER = '#C0C0C0';
export const BRONZE = '#CD7F32';
export const DARK_BG = '#1a1025';
export const DARK_BG2 = '#2d1b4e';
export const ACCENT = '#7C3AED';
export const GRADIENT_GOLD = 'linear-gradient(135deg, #FFD700, #FF8C00)';
export const GRADIENT_DARK = 'linear-gradient(135deg, #1a1025, #2d1b4e)';

// ── Theme-aware color maps ──
const DARK = {
  bg: '#1a1025',
  bgCard: 'rgba(255,255,255,0.02)',
  bgCardHover: 'rgba(255,215,0,0.04)',
  bgHeader: 'linear-gradient(135deg, #1a1025 0%, #2d1b4e 50%, #1a1025 100%)',
  headerBorder: '#FFD70022',
  text: '#e0e0e0',
  textMuted: '#999',
  textInverse: '#1a1025',
  gold: '#FFD700',
  goldDark: '#B8860B',
  goldGradient: 'linear-gradient(135deg, #FFD700, #FF8C00)',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
  accent: '#7C3AED',
  border: 'rgba(255,215,0,0.15)',
  borderLight: 'rgba(255,255,255,0.06)',
  cardBg: 'rgba(255,255,255,0.02)',
  progressBg: 'rgba(255,255,255,0.08)',
  success: '#22C55E',
  error: '#ff6b6b',
  errorBg: 'rgba(255,0,0,0.1)',
  goldShadow: '#FFD70044',
  elementBg: 'rgba(255,255,255,0.05)',
  decorativeCircle: (alpha) => `radial-gradient(circle, rgba(255,215,0,${alpha}), transparent 70%)`,
  incompleteBg: 'rgba(255,255,255,0.08)',
  incompleteBorder: 'rgba(255,255,255,0.15)',
  incompleteText: 'rgba(255,255,255,0.3)',
};

const LIGHT = {
  bg: '#FFF8F0',
  bgCard: '#FFFFFF',
  bgCardHover: 'rgba(184,134,11,0.06)',
  bgHeader: 'linear-gradient(135deg, #FFFFFF 0%, #FFF5E6 50%, #FFF8F0 100%)',
  headerBorder: 'rgba(184,134,11,0.2)',
  text: '#1a1025',
  textMuted: '#6B7280',
  textInverse: '#FFFFFF',
  gold: '#B8860B',
  goldDark: '#8B6914',
  goldGradient: 'linear-gradient(135deg, #DAA520, #B8860B)',
  silver: '#6B7280',
  bronze: '#CD7F32',
  accent: '#7C3AED',
  border: 'rgba(184,134,11,0.15)',
  borderLight: 'rgba(0,0,0,0.08)',
  cardBg: '#FFFFFF',
  progressBg: 'rgba(0,0,0,0.06)',
  success: '#16A34A',
  error: '#DC2626',
  errorBg: 'rgba(220,38,38,0.08)',
  goldShadow: 'rgba(184,134,11,0.3)',
  elementBg: 'rgba(0,0,0,0.03)',
  decorativeCircle: (alpha) => `radial-gradient(circle, rgba(184,134,11,${alpha}), transparent 70%)`,
  incompleteBg: 'rgba(0,0,0,0.04)',
  incompleteBorder: 'rgba(0,0,0,0.1)',
  incompleteText: 'rgba(0,0,0,0.2)',
};

// ── Theme hook — use in all champion pages ──
export function useChampionTheme() {
  const { isDark } = useTheme();
  return { isDark, ...(isDark ? DARK : LIGHT) };
}

// ── Trophy SVG inline ──
export function TrophyIcon({ size = 48, color = GOLD }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M20 8h24v4c0 6.6-5.4 12-12 12s-12-5.4-12-12V8z" fill={color} stroke={GOLD_DARK} strokeWidth="2" />
      <path d="M16 12H8v4c0 4.4 3.6 8 8 8h2" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M48 12h8v4c0 4.4-3.6 8-8 8h-2" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <rect x="26" y="32" width="12" height="14" rx="2" fill={color} stroke={GOLD_DARK} strokeWidth="1.5" />
      <path d="M22 46h20l3 6c.8 1.5-.3 3-2 3H21c-1.7 0-2.8-1.5-2-3l3-6z" fill={color} stroke={GOLD_DARK} strokeWidth="1.5" />
      <rect x="30" y="24" width="4" height="10" rx="1" fill={GOLD_DARK} />
      <circle cx="26" cy="4" r="3" fill={color} opacity="0.6">
        <animate attributeName="cy" values="4;0;4" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="44" cy="3" r="2" fill={color} opacity="0.5">
        <animate attributeName="cy" values="3;-2;3" dur="2.5s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.5;0;0.5" dur="2.5s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

// ── Progress bar with gold gradient + shimmer ──
export function ChampionProgress({ value = 0, label, showPercent = true, height = 12 }) {
  const t = useChampionTheme();
  return (
    <div>
      {label && (
        <div className="d-flex justify-content-between small mb-1" style={{ color: t.textMuted }}>
          <span>{label}</span>
          {showPercent && <span style={{ color: t.gold, fontWeight: 700 }}>{value}%</span>}
        </div>
      )}
      <div
        className="progress"
        style={{
          height: `${height}px`,
          backgroundColor: t.progressBg,
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      >
        <div
          className="progress-bar"
          style={{
            width: `${value}%`,
            background: t.goldGradient,
            borderRadius: '20px',
            boxShadow: `0 0 12px ${t.gold}66`,
            transition: 'width 0.8s ease',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Shimmer effect */}
          <div
            style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
              animation: 'shimmer 2s infinite',
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Button with gold gradient ──
export function GoldButton({ children, onClick, disabled, outline, className = '', style = {} }) {
  const t = useChampionTheme();
  return (
    <button
      className={`btn ${className}`}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: outline ? 'transparent' : t.goldGradient,
        border: outline ? `2px solid ${t.gold}` : 'none',
        color: outline ? t.gold : t.textInverse,
        fontWeight: 700,
        borderRadius: '12px',
        padding: '10px 24px',
        boxShadow: outline ? 'none' : `0 4px 20px ${t.goldShadow}`,
        transition: 'all 0.3s ease',
        ...style,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 6px 28px ${t.gold}66`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = outline ? 'none' : `0 4px 20px ${t.goldShadow}`;
      }}
    >
      {children}
    </button>
  );
}

// ── Gold badge ──
export function ChampionBadge({ children, color, style = {} }) {
  const t = useChampionTheme();
  const c = color || t.gold;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: `linear-gradient(135deg, ${c}22, ${c}11)`,
        border: `1px solid ${c}44`,
        color: c,
        borderRadius: '20px',
        padding: '4px 14px',
        fontSize: '0.8rem',
        fontWeight: 600,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// ── Glowing text ──
export function GlowText({ children, as: Tag = 'span', color, style = {} }) {
  const t = useChampionTheme();
  const c = color || t.gold;
  return (
    <Tag
      style={{
        color: c,
        textShadow: t.isDark ? `0 0 20px ${c}66, 0 0 40px ${c}33` : 'none',
        fontWeight: 800,
        letterSpacing: '1px',
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}

// ── Podium 3-level display ──
export function Podium({ steps = [], currentStep = -1 }) {
  const t = useChampionTheme();
  const heights = [200, 150, 110];
  const colors = [t.gold, t.silver, t.bronze];
  const labels = ['#1', '#2', '#3'];

  if (steps.length === 0) steps = ['Step 1', 'Step 2', 'Step 3'];

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: '16px', padding: '24px 0' }}>
      {[1, 0, 2].map((origIdx, displayIdx) => {
        const idx = displayIdx;
        const stepIdx = [1, 0, 2][idx];
        const isCompleted = stepIdx <= currentStep;
        const isCurrent = stepIdx === currentStep;
        return (
          <div key={idx} style={{ textAlign: 'center' }}>
            <div style={{
              background: isCompleted ? colors[stepIdx] : t.incompleteBg,
              border: `2px solid ${isCompleted ? colors[stepIdx] : t.incompleteBorder}`,
              borderRadius: '16px 16px 4px 4px',
              width: '90px',
              height: `${heights[stepIdx]}px`,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              boxShadow: isCurrent ? `0 0 30px ${colors[stepIdx]}55` : 'none',
              transition: 'all 0.5s ease',
            }}>
              {isCompleted ? (
                <i className="bi bi-check-circle-fill" style={{ fontSize: '1.5rem', color: isCurrent ? '#fff' : 'rgba(0,0,0,0.5)' }} />
              ) : (
                <i className="bi bi-clock" style={{ fontSize: '1.5rem', color: t.incompleteText }} />
              )}
              <span style={{
                fontSize: '0.7rem', fontWeight: 700, marginTop: '6px',
                color: isCompleted ? (isCurrent ? '#fff' : 'rgba(0,0,0,0.6)') : t.incompleteText,
              }}>
                {steps[stepIdx]}
              </span>
            </div>
            <div style={{
              fontSize: '0.75rem', fontWeight: 800, marginTop: '6px',
              color: colors[stepIdx],
            }}>
              {labels[stepIdx]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Confetti CSS animation wrapper ──
export function Confetti({ children, active = true }) {
  const confettiPieces = Array.from({ length: 30 }, (_, i) => ({
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 3}s`,
    duration: `${2 + Math.random() * 3}s`,
    color: [GOLD, SILVER, BRONZE, ACCENT, '#FF6B6B', '#4ECDC4'][i % 6],
    size: `${4 + Math.random() * 8}px`,
  }));

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {active && confettiPieces.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: p.left,
            top: '-10px',
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            animation: `confetti-fall ${p.duration} linear ${p.delay} infinite`,
            opacity: 0.8,
            zIndex: 0,
          }}
        />
      ))}
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  );
}

// ── Star icon (reusable) ──
export function Star({ size = 16, filled = true, color = GOLD }) {
  return (
    <i
      className={`bi ${filled ? 'bi-star-fill' : 'bi-star'}`}
      style={{ fontSize: `${size}px`, color }}
    />
  );
}

// ── Crown icon SVG ──
export function CrownIcon({ size = 24, color = GOLD }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M2 19h20v3H2v-3zM3 8l4 6 5-8 5 8 4-6-2 8H5L3 8z" />
      <circle cx="7" cy="4" r="1.5" fill={color} opacity="0.8" />
      <circle cx="12" cy="2" r="1.5" fill={color} opacity="0.8" />
      <circle cx="17" cy="4" r="1.5" fill={color} opacity="0.8" />
    </svg>
  );
}

// ── Medal icon SVG ──
export function MedalIcon({ size = 48, color = GOLD, type = 'gold' }) {
  const colors = { gold: GOLD, silver: SILVER, bronze: BRONZE };
  const c = colors[type] || GOLD;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="36" r="18" fill={c} stroke={type === 'gold' ? GOLD_DARK : '#888'} strokeWidth="2" />
      <text x="32" y="42" textAnchor="middle" fill={type === 'gold' ? DARK_BG : '#333'} fontSize="14" fontWeight="900">★</text>
      <path d="M22 12h20l-2 14H24l-2-14z" fill={c} stroke={type === 'gold' ? GOLD_DARK : '#888'} strokeWidth="1.5" />
      <rect x="28" y="6" width="8" height="8" rx="2" fill={c} />
    </svg>
  );
}

// ── Section card wrapper ──
export function ChampionCard({ children, style = {} }) {
  const t = useChampionTheme();
  return (
    <div
      style={{
        background: t.cardBg,
        border: `1px solid ${t.border}`,
        borderRadius: '16px',
        padding: '24px',
        ...style,
      }}
    >
      {children}
    </div>
  );
}
